import { useCallback } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { GeogebraComponent } from '@apps/geogebra';
import { ElementToolbar } from '@pm2/element-toolbar';
import { scrollDirectionStore } from '@pm2/react-utils';
import { usePM2DisplayContext, pm2ConfigStore } from '@pm2/store';
import { usePm2Modal } from '@pm2/ui-lightbox';
import { componentFrameworkStore } from '@provider/component-framework';
import { useComponentEntry, UniversalLightboxCard, UniversalHarmonicaCard, UniversalFinishedCard } from '@utils/component-framework';
import { useToggleState, SizeTransition } from '@utils/react';
import { EmbedContent } from './comp.embed-integration.js';
import { PM2GeogebraComponent } from './comp.root.js';
import { card, contentTransitionDurationMs, content } from './comp.router.style.js';
import { ScrambledCard } from './comp.scrambled.js';
import { jsx } from 'react/jsx-runtime';

function Router(props) {
  const modal = usePm2Modal();
  const {
    inLightbox
  } = usePM2DisplayContext();
  const resolvedDisplayMode = props.displayMode;
  const isEntryShared = useComponentEntry(props.component.entryKey, {
    select: entry => entry?.envelope.shared
  });
  const audience = pm2ConfigStore.useSelector(state => state.config.theme.targetAudience);
  const [expanded, expansionActions] = useToggleState();
  const state = (() => {
    if (props.isScrambled) {
      return "scrambled";
    }
    if (inLightbox) {
      return "standalone";
    }
    if (!expanded) {
      if (resolvedDisplayMode === "embed" && isEntryShared.data) {
        return "finished";
      }
      return resolvedDisplayMode;
    }
    return "embed";
  })();
  const onShareWithTeacher = useCallback(() => {
    expansionActions.disable();
  }, [expansionActions]);
  const onLightboxActivate = useCallback(() => {
    modal.fromComponentFrameworkNode(/*#__PURE__*/jsx(PM2GeogebraComponent, {
      ...props
    }), "component-framework.geogebra");
  }, [modal, props]);
  const onActivate = useCallback(() => {
    if (resolvedDisplayMode === "lightbox") {
      onLightboxActivate();
      return;
    }
    componentFrameworkStore.dispatch(componentFrameworkStore.actions.activateComponent(props.component.envelope.ID));
    expansionActions.enable();

    // Force the header to collapse upon opening the component
    scrollDirectionStore.dispatch(scrollDirectionStore.actions.setDirection("down"));
  }, [expansionActions, onLightboxActivate, props.component.envelope.ID, resolvedDisplayMode]);
  const onDeactivate = useCallback(() => {
    expansionActions.disable();
    componentFrameworkStore.dispatch(componentFrameworkStore.actions.deactivateComponent(props.component.envelope.ID));
  }, [expansionActions, props.component.envelope.ID]);
  const onCollapse = useCallback(() => {
    expansionActions.disable();
    onDeactivate();
  }, [expansionActions, onDeactivate]);
  const content$1 = (() => {
    switch (state) {
      case "embed":
        return /*#__PURE__*/jsx(EmbedContent, {
          ...props,
          onActivate: onActivate,
          onDeactivate: onDeactivate,
          onShareWithTeacher: onShareWithTeacher,
          onCollapse: resolvedDisplayMode === "harmonica" || isEntryShared.data && resolvedDisplayMode === "embed" ? onCollapse : undefined
        });
      case "standalone":
        return /*#__PURE__*/jsx(GeogebraComponent, {
          context: "lightbox",
          component: props.component,
          node: props.node,
          onResetEntry: props.onResetEntry
        });
      case "finished":
        return /*#__PURE__*/jsx(UniversalFinishedCard, {
          className: card,
          title: props.component.envelope.Title,
          onClick: onActivate,
          children: /*#__PURE__*/jsx(UniversalFinishedCard.ContextMenu, {
            children: /*#__PURE__*/jsx(ElementToolbar, {
              node: props.node
            })
          })
        });
      case "harmonica":
        return /*#__PURE__*/jsx(UniversalHarmonicaCard, {
          className: card,
          title: props.component.envelope.Title,
          description: props.richtextDescription,
          audience: audience,
          hasShared: isEntryShared.data,
          badgeImageSrc: props.previewImage,
          indexOrLevel: props.indexOrLevel,
          onClick: onActivate,
          children: /*#__PURE__*/jsx(UniversalHarmonicaCard.ContextMenu, {
            children: /*#__PURE__*/jsx(ElementToolbar, {
              node: props.node
            })
          })
        });
      case "lightbox":
        return /*#__PURE__*/jsx(UniversalLightboxCard, {
          className: card,
          title: props.component.envelope.Title,
          description: props.lightboxDescription,
          audience: audience,
          hasShared: isEntryShared.data,
          badgeImageSrc: props.previewImage,
          indexOrLevel: props.indexOrLevel,
          onClick: onLightboxActivate,
          children: /*#__PURE__*/jsx(UniversalLightboxCard.ContextMenu, {
            children: /*#__PURE__*/jsx(ElementToolbar, {
              node: props.node
            })
          })
        });
      case "scrambled":
        return /*#__PURE__*/jsx(ScrambledCard, {
          className: card,
          title: props.component.envelope.Title
        });
    }
  })();
  return /*#__PURE__*/jsx(TransitionGroup, {
    component: null,
    children: /*#__PURE__*/jsx(SizeTransition, {
      axis: "vertical",
      swappable: true,
      classNames: content,
      timeout: contentTransitionDurationMs,
      delayMs: {
        enter: 10,
        exit: 0
      },
      onEnter: () => document.documentElement.style.setProperty("overflow-anchor", "none"),
      onEntered: () => document.documentElement.style.setProperty("overflow-anchor", ""),
      children: ref => /*#__PURE__*/jsx("div", {
        ref: ref,
        "data-node-id": props.component.envelope.ID,
        className: content,
        children: content$1
      })
    }, state)
  });
}

export { Router };
//# sourceMappingURL=comp.router.js.map
